import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';

/**
 * Intercepteur Idempotence — ajoute un header `X-Idempotency-Key` aléatoire
 * sur toutes les mutations sortantes (POST / PUT / PATCH / DELETE) qui n'en
 * portent pas déjà un.
 *
 * Pourquoi : le backend Dinki applique l'idempotence sur le préfixe
 * `/api/v{n}/…` via {@code IdempotencyFilter}. Sans clé, un double-clic ou
 * une retransmission réseau peut créer un doublon (double paiement, double
 * refund, double commande). L'envoi systématique d'une clé unique transforme
 * tout retry en replay silencieux côté serveur (réponse stockée rejouée à
 * l'identique avec `X-Idempotent-Replay: 1`).
 *
 * Ce que cet intercepteur fait :
 *  - Pour les méthodes mutantes uniquement (POST/PUT/PATCH/DELETE).
 *  - Uniquement si l'appel cible une URL `/api/v…` (les autres routes — assets,
 *    services tiers — ne sont pas concernées par le filtre backend).
 *  - Génère un UUID v4 via `crypto.randomUUID()` (fallback Math.random pour
 *    environnements legacy).
 *
 * Ce qu'il NE fait PAS :
 *  - Il n'écrase JAMAIS une clé déjà fournie par l'appelant (typiquement la
 *    queue offline qui pose `mut-{id}-{ts}` pour pouvoir corréler ses retries).
 *  - Il ne sait pas distinguer "double-clic involontaire" de "deux mutations
 *    intentionnelles" — c'est l'appelant qui décide de réutiliser ou non sa
 *    clé. Par défaut chaque nouvelle requête en a une fraîche.
 *
 * Ordre : doit s'exécuter APRÈS jwtInterceptor (pour que le header
 * Authorization soit déjà posé) mais avant errorInterceptor. Voir
 * `app.config.ts` pour l'ordre effectif.
 */
export const idempotencyInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isMutation(req)) {
    return next(req);
  }
  if (!isApiRoute(req)) {
    return next(req);
  }
  if (hasIdempotencyKey(req)) {
    return next(req);
  }
  // Pas de clé sur les uploads multipart (FormData) : le filtre backend mettrait
  // le corps en cache pour le hash et consommerait le flux multipart, cassant le
  // parsing des parties (« Required part 'file' is not present »).
  if (typeof FormData !== 'undefined' && req.body instanceof FormData) {
    return next(req);
  }

  const key = generateKey();
  const cloned = req.clone({ setHeaders: { 'X-Idempotency-Key': key } });
  return next(cloned);
};

// ─── Helpers ──────────────────────────────────────────────────────────────

const MUTATION_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function isMutation(req: HttpRequest<unknown>): boolean {
  return MUTATION_METHODS.has(req.method.toUpperCase());
}

/**
 * Le filtre backend ne s'applique qu'au préfixe `/api/v{n}/…`. On évite
 * d'ajouter la clé sur des appels à des CDN, à MinIO direct ou à des assets
 * statiques pour ne pas polluer leurs en-têtes.
 */
function isApiRoute(req: HttpRequest<unknown>): boolean {
  return /\/api\/v\d+\//.test(req.url);
}

function hasIdempotencyKey(req: HttpRequest<unknown>): boolean {
  const value = req.headers.get('X-Idempotency-Key');
  return value !== null && value.trim().length > 0;
}

/**
 * Génère une clé d'idempotence. Privilégie `crypto.randomUUID()` (standard
 * navigateur moderne + Node 19+). Fallback sur un préfixe + horodatage +
 * Math.random pour les environnements anciens (build pré-rendu, tests Karma
 * legacy, etc.). La forme du fallback reste compatible avec la contrainte
 * backend `length <= 100`.
 */
function generateKey(): string {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID) {
      return c.randomUUID();
    }
  } catch {
    // ignore
  }
  // Fallback : "dk-{ms}-{random}" — ~30 chars, suffisamment unique pour un
  // client unique (l'unicité globale est garantie par la combinaison avec
  // l'identité de l'utilisateur côté serveur).
  return (
    'dk-' +
    Date.now().toString(36) +
    '-' +
    Math.random().toString(36).slice(2, 12)
  );
}
